<template>
    <div class="grid">
        <DownloadButton
            v-if="!isPasswordRequired"
            @click="startDownload"
            target="_blank"
            variant="yellow"
            download>
            Download
        </DownloadButton>

        <Button v-else @click="openPasswordDialog" variant="yellow">Download</Button>

        <div
            v-if="isStarted"
            class="mt-3 rounded bg-muted p-3 text-xs font-semibold text-muted-foreground">
            Your download has started.
        </div>

        <Dialog :open="showPasswordDialog" @update:open="showPasswordDialog = $event">
            <DialogTrigger />
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Password Required</DialogTitle>
                    <DialogDescription>
                        Please enter the password provided by the sender to download these files.
                    </DialogDescription>
                </DialogHeader>
                <div class="flex flex-col gap-4 py-4">
                    <div class="flex flex-col gap-2">
                        <Label for="password">Password</Label>
                        <Input
                            id="password"
                            v-model="password"
                            type="password"
                            placeholder="Enter password"
                            :class="{ 'border-destructive': passwordError }"
                            @keyup.enter="submitPassword" />
                        <p v-if="passwordError" class="text-xs text-destructive">
                            {{ passwordError }}
                        </p>
                    </div>
                </div>
                <DialogFooter class="flex justify-between sm:justify-between">
                    <Button variant="outline" @click="closePasswordDialog">Cancel</Button>
                    <Button
                        variant="yellow"
                        @click="submitPassword"
                        :disabled="isSubmitting || isDownloading">
                        <span v-if="isSubmitting || isDownloading" class="mr-2">
                            <svg
                                class="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24">
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </span>
                        Download
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import DownloadButton from '@/components/core/download-button/DownloadButton.vue'
import { useDownloadFile } from '@/composables/use-download-file'

interface Props {
    shortUrlId: string
    isPasswordRequired?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    isPasswordRequired: false,
})

const isStarted = ref(false)
const showPasswordDialog = ref(false)
const password = ref('')
const passwordError = ref('')
const isSubmitting = ref(false)

const downloadUrl = computed(() => {
    const baseUrl = `/api/v1/short-urls/${props.shortUrlId}/file-sharing/downloads`
    if (!props.isPasswordRequired || !password.value) {
        return baseUrl
    }
    return `${baseUrl}?password=${encodeURIComponent(password.value)}`
})

// For non-password downloads, we can use the DownloadButton directly
function startDownload() {
    isStarted.value = true
}

// For password-protected downloads, we'll use the composable
const { download, isDownloading } = useDownloadFile(downloadUrl)

// Watch for download completion and update UI
watch(isDownloading, (value) => {
    if (!value && isSubmitting.value) {
        // Download finished
        isStarted.value = true
        setTimeout(() => {
            isSubmitting.value = false
            showPasswordDialog.value = false
        }, 1000)
    }
})

function openPasswordDialog() {
    showPasswordDialog.value = true
    password.value = ''
    passwordError.value = ''
}

function closePasswordDialog() {
    showPasswordDialog.value = false
    password.value = ''
    passwordError.value = ''
}

function submitPassword() {
    if (!password.value) {
        passwordError.value = 'Password is required'
        return
    }

    isSubmitting.value = true

    // Use the composable to handle the download
    download()
}
</script>

<style scoped></style>
